import { useEffect, useRef } from 'react';
import { useMeasurementWizardStore } from '@/store/useMeasurementWizardStore';

/**
 * Attaches a `beforeunload` listener that warns the user before leaving the page.
 * Also fires a `sendBeacon` on `pagehide` when a measurement is in flight, so the
 * backend can log the interruption even if the page is torn down mid-request.
 *
 * @param active - Whether the guard is currently active. When false, both listeners
 *                 are removed. This allows callers to enable/disable based on
 *                 measurement phase.
 */
export function useBeforeUnloadGuard(active: boolean): void {
  // Keep a stable ref to `active` so the pagehide handler can read the latest value
  // without needing to re-register every render.
  const activeRef = useRef(active);
  useEffect(() => {
    activeRef.current = active;
  });

  useEffect(() => {
    if (!active) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    const handlePageHide = () => {
      if (!activeRef.current) return;

      const { measurementInFlight, testId } = useMeasurementWizardStore.getState();
      if (!measurementInFlight) return;

      // sendBeacon is fire-and-forget — ideal for page-unload notifications.
      // The payload deliberately excludes patient data (only metadata).
      if (typeof navigator.sendBeacon === 'function') {
        const payload = JSON.stringify({
          testId,
          phase: measurementInFlight.phase,
          interruptedAt: new Date().toISOString(),
          startedAt: new Date(measurementInFlight.startedAt).toISOString(),
          deviceUid: measurementInFlight.deviceUid,
        });
        navigator.sendBeacon(
          '/api/v1/tests/interruption',
          new Blob([payload], { type: 'application/json' }),
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [active]);
}
